import * as moment from 'moment/moment.js'
import { isEqual } from 'lodash'

import {
  getGlobalSearch,
  getGlobalSearchLimit,
  isPaidUser,
} from './freeVersionBlock'
import { getLoggedUser } from './userAuth'
import { TIMESTAMP_LIFETIME } from './consts.mjs'

export const saveRecentProject = (project) => {
  const user = getLoggedUser()
  localStorage.setItem(
    `recent_project_${user?.id}`,
    JSON.stringify({
      id: project.id,
      domain: project.domain,
    }),
  )
}

export const getRecentProject = (firstProject) => {
  const user = getLoggedUser()
  const savedProject = JSON.parse(
    localStorage.getItem(`recent_project_${user?.id}`),
  )
  if (savedProject && savedProject.id) {
    return savedProject
  }
  if (firstProject) saveRecentProject(firstProject)
  return firstProject
}

export const removeRecentProject = (projectId) => {
  const user = getLoggedUser()
  const savedProject = getRecentProject() || {}
  if (savedProject.id === projectId)
    return localStorage.removeItem(`recent_project_${user?.id}`)
}

export const hideUpdateBillingInfoAlert = () => {
  const user = getLoggedUser()
  localStorage.setItem(`ub_update_billing_info_alert_${user?.id}`, 'hide')
}

export const shouldHideUpdateBillingInfoAlert = () => {
  const user = getLoggedUser()
  const billingInfoAlertStatus = localStorage.getItem(
    `ub_update_billing_info_alert_${user?.id}`,
  )

  return billingInfoAlertStatus === 'hide'
}

export const removeUpdateBillingInfoAlert = () => {
  const user = getLoggedUser()
  localStorage.removeItem(`ub_update_billing_info_alert_${user?.id}`)
}

export const saveUnloggedUserProject = (project) => {
  localStorage.setItem('unlogged_user_project', JSON.stringify(project))
}

export const saveUnloggedUserProjectCheckout = (project) => {
  localStorage.setItem(
    'unlogged_user_project_checkout',
    JSON.stringify(project),
  )
}

export const getUnloggedUserProjectCheckout = () => {
  return JSON.parse(localStorage.getItem('unlogged_user_project_checkout'))
}

export const hasUnloggedUserProjectCheckout = () =>
  localStorage.getItem('unlogged_user_project_checkout') !== null

export const removeUnloggedUserProjectCheckout = () => {
  localStorage.removeItem('unlogged_user_project_checkout')
}

export const hasUnloggedUserProject = () =>
  localStorage.getItem('unlogged_user_project') !== null

export const getUnloggedUserProject = () => {
  return JSON.parse(localStorage.getItem('unlogged_user_project'))
}

export const removeUnloggedUserProject = () => {
  localStorage.removeItem('unlogged_user_project')
}

export const saveGoogleStatus = (projectId, status = {}) => {
  localStorage.setItem(`google_status_${projectId}`, JSON.stringify(status))
}

export const loadGoogleStatus = (projectId) => {
  return JSON.parse(localStorage.getItem(`google_status_${projectId}`)) || {}
}

export const clearGoogleStatus = (projectId, fields) => {
  let status = loadGoogleStatus(projectId)
  fields.forEach((item) => {
    status = {
      ...status,
      [item]: {
        ...status[item],
        error: null,
      },
    }
  })
  saveGoogleStatus(projectId, status)
}

export const deleteGoogleStatus = (projectId) => {
  localStorage.removeItem(`google_status_${projectId}`)
}

export const deleteAllGoogleStatus = () => {
  const keyPrefix = 'google_status_'
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i)
    if (key.startsWith(keyPrefix)) {
      localStorage.removeItem(key)
    }
  }
}

export const saveNewProjectStatus = (projectId, show = false) => {
  localStorage.setItem(`new_project_tooltip_${projectId}`, show)
}

export const loadNewProjectStatus = (projectId) => {
  return localStorage.getItem(`new_project_tooltip_${projectId}`)
}

export const saveSiteAuditCronjob = (
  domain,
  mode,
  locId,
  lang,
  siteAuditData,
) => {
  localStorage.setItem(
    'site_audit_cronjob',
    JSON.stringify({
      domain,
      mode,
      locId,
      lang,
      siteAuditData,
      updated: new Date(), // .getTime()
    }),
  )
}
export const getSiteAuditCronjob = () =>
  JSON.parse(localStorage.getItem('site_audit_cronjob'))

export const removeSiteAuditCronjob = () =>
  localStorage.removeItem('site_audit_cronjob')

export const saveSelection = (report, data) => {
  sessionStorage.setItem(`ub_selection_${report}`, JSON.stringify(data))
}

export const loadSelection = (report) => {
  const data = JSON.parse(sessionStorage.getItem(`ub_selection_${report}`))
  removeSelection(report)
  return data
}

export const removeSelection = (report) => {
  sessionStorage.removeItem(`ub_selection_${report}`)
}

export const hasValidPreviousSelection = (report, data) => {
  const selection = JSON.parse(sessionStorage.getItem(`ub_selection_${report}`))
  if (!selection) return false
  const hasUnEqual = Object.keys(data).some((key) => {
    if (!isEqual(data[key], selection[key])) return true
    return false
  })
  return !hasUnEqual
}

export const setDailyReportInfo = (data) => {
  const reports = [
    {
      used: 'reports_used',
      limit: 'reports_limits',
    },
  ]

  const currentReport = JSON.parse(localStorage.getItem('daily_report'))
  const report = reports.reduce((acc, item) => {
    const { used, limit } = item
    if (typeof data[limit] === 'number') acc[limit] = data[limit]
    if (typeof data[used] === 'number') acc[used] = data[used]
    else acc[used] = 0
    if (
      typeof data[limit] !== 'number' &&
      currentReport &&
      Object.keys(currentReport).length !== 0 &&
      currentReport[limit]
    )
      acc[limit] = currentReport[limit]
    return acc
  }, {})

  localStorage.setItem('daily_report', JSON.stringify(report))
}

export const getDailyReportInfo = (type = '') => {
  const prefix = type ? `${type}_` : ''
  const report = localStorage.getItem('daily_report')
  let dailyReport
  const isPaid = isPaidUser()
  if (isPaid) {
    if (report) {
      dailyReport = JSON.parse(report)
    } else {
      dailyReport = {
        [`${prefix}reports_limits`]: getGlobalSearchLimit(type),
        [`${prefix}reports_used`]: 0,
      }
    }
  } else {
    dailyReport = {
      [`${prefix}reports_limits`]: getGlobalSearchLimit(type),
      [`${prefix}reports_used`]: getGlobalSearch(type).list.length,
    }
  }

  return dailyReport
}

export const getCurrentTimestamp = () => {
  const currentTimestamp = moment().utc().unix()

  if (hasCurrentTimestamp()) {
    const reportData = JSON.parse(localStorage.getItem('current_timestamp'))

    if (currentTimestamp - reportData.ts > TIMESTAMP_LIFETIME) {
      reportData.ts = currentTimestamp
      localStorage.setItem('current_timestamp', JSON.stringify(reportData))
    }

    return reportData.ts
  }
  localStorage.setItem(
    'current_timestamp',
    JSON.stringify({
      ts: currentTimestamp,
    }),
  )

  return currentTimestamp
}

export const hasCurrentTimestamp = () =>
  !!localStorage.getItem('current_timestamp')

export const setCurrentTimestamp = (reportData) => {
  if (hasCurrentTimestamp() && reportData) {
    const oldData = JSON.parse(localStorage.getItem('current_timestamp'))

    const isNewReport = Object.keys(reportData).some(
      (key) => !isEqual(reportData[key], oldData[key]),
    )

    if (!isNewReport) return
  }

  localStorage.setItem(
    'current_timestamp',
    JSON.stringify({
      ...reportData,
      ts: moment().utc().unix(),
    }),
  )
}

export const clearToken = () => {
  window.localStorage.removeItem('token')
  window.localStorage.removeItem('token_ttl')
}

export const saveAdblockDetectModalStatus = () => {
  localStorage.setItem('closed_ad_block_detect_modal', true)
}

export const closedAdblockDetectModal = () =>
  localStorage.getItem('closed_ad_block_detect_modal') === 'true'

export const removeAdblockDetectModalStatus = () => {
  localStorage.removeItem('closed_ad_block_detect_modal')
}

export const removeAIWPhaseOutModalStatus = () => {
  localStorage.removeItem('closed_aiw_phase_out_modal')
}
